import { useState, useRef, useCallback, memo, useMemo } from "react"
import Popper from "@mui/material/Popper"
import Paper from "@mui/material/Paper"
import { useLocalization } from "@tm/localization"
import { Memo, MemoType } from "@tm/models"
import { MemoTooltipContent } from "./MemoTooltipContent"
import { Icon } from "../../../../muiStyled/DataDisplay/Icons"
import { Typography, Box } from "@mui/material"
import { Modifier, Placement } from "@popperjs/core"
import { round } from "lodash"

type Props = {
    memos: Memo[]
    iconName: string
}
const POPPERTOWINDOWGAP = 4
export const MemoTooltip = memo(({ memos, iconName }: Props) => {
    const { translateText } = useLocalization()
    const timeoutRef = useRef<number | null>(null);

    if (!iconName) { return null }

    const memosFiltered = () => memos?.some(
        m =>
            m.type === MemoType.Hyperlink ||
            m.type === MemoType.AdditionalDescription
    )

    if (!memosFiltered) { return null }

    const [open, setOpen] = useState(false)
    const [maxHeight, setMaxHeight] = useState<number | null>(null)
    const iconRef = useRef<HTMLDivElement>(null)

    const updateMaxHeight = (placement: Placement) => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect()
            const viewportHeight = window.innerHeight

            let availableHeight = 0
            if (placement.startsWith("bottom")) {
                availableHeight = viewportHeight - (rect.bottom + POPPERTOWINDOWGAP);
            } else if (placement.startsWith("top")) {
                availableHeight = rect.top - POPPERTOWINDOWGAP;
            }

            setMaxHeight(round(availableHeight, 0))
        }
    }

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        setOpen(true)
    }

    const handleMouseLeave = () => {
        timeoutRef.current = window.setTimeout(() => {
            setOpen(false)
        }, 125)

    }

    const updateHeightModifier: Modifier<"updateHeightModifier", {}> = {
        name: 'updateHeightModifier',
        enabled: true,
        phase: 'beforeWrite',
        requires: ["computeStyles"],
        fn({ state }) {
            if (state.placement === "bottom" || state.placement === "top") {
                updateMaxHeight(state.placement)
            }
        },
    }

    return (
        <>
            <Box
                ref={iconRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{ display: "inline-block" }}
            >
                <Icon color={open ? "primary" : "highlight"} name={iconName} />
            </Box>
            <Popper
                open={open}
                anchorEl={iconRef.current}
                placement="bottom"
                sx={{
                    zIndex: 11,
                    border: theme => `1px solid ${theme.palette.primary.main}`,
                    borderRadius: theme => theme.radius?.default,
                }}

                modifiers={[
                    updateHeightModifier,
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            fallbackPlacements: ['top', 'bottom'],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        enabled: false,
                        options: {
                            padding: 8,
                            tether: false,
                        },
                    }
                ]}
            >
                <Paper
                    sx={{
                        p: "12px",
                        maxHeight: maxHeight ? `${maxHeight}px` : "80vh",
                        overflowY: "auto",
                        backgroundColor: theme => theme.palette.common.white,
                        borderRadius: theme => theme.radius?.default
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    elevation={8}
                >
                    <Box>
                        <Typography variant="h4">{translateText(180)}</Typography>
                        <MemoTooltipContent memos={memos} />
                    </Box>
                </Paper>

            </Popper>
        </>
    )
})

